import { configureStore, isRejectedWithValue } from '@reduxjs/toolkit';
import authReducer, {
  updateProfileState,
  updateToken,
} from '../features/auth/authSlice';
import jobCreationReducer from '../features/job/creation/jobCreationSlice';
import mailCreationReducer from '../features/mailbox/creation/mailCreationSlice';
import messageCreationReducer from '../features/mailbox/creation/components/messageCreationSlice';
import candidatesReducer from '../features/candidates/candidatesSlice';
import formDesignReducer from '../features/forms/formsDesign/FormDesignSlice';
import NotesReducer from '../features/kanban/NotesDataSlice';
import stagesReducer from '../features/job/StagesDataSlice';
import notificationsReducer from '../features/Setting/Notifications/Apis/notificationsDataSlice';
import paginationReducer from '../app/components/pagination/paginationSlice';
import { apiSlice } from './services/apiSlice';
import campaignsReducer from '../features/campaigns/CampaignDataSlice';
import childCompanyReducer from '../features/Setting/ChildCompanies/ChildCompanyDataSlice';
import childCompanyHeaderReducer from '../features/Setting/ChildCompanies/ChildCompanyHeaderDataSlice';
import { toast } from 'react-toastify';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { occyLs } from '../utils/localStorage';
import emailTemplateReducer from '../features/users/emailTemplateSlice';
import isObject from 'lodash/isObject';
import DocumentReducer from '../features/Setting/OfferTemplate/DocumentSlice';
import creditCompanyReducer from './layout/header/HeaderDataSlice';
import WizardDataReducer from '../features/dashboard/listing/WizardDataSlice';
import AccountReducer from '../features/Setting/AccountCalender/AccountSlice';
import { processValidationErrors } from '../features/Setting/utils';
import permisssionReducer, {
  setForceLoginPermission,
  setOOOPermission,
} from '../midleware/permisssionReducer';
import permissionGateReducer from '../permission-system/store/permissionGateSlice';
import { PERMISSION_DENIED_CODE } from '../permission-system/middleware/permission-base-query';
import { permissionDeniedListener } from '../permission-system/middleware/permission-denied-listener';
import { forceLoginError, whitelistedErrors } from '../utils/helper';
import candidatePoolingReducer from '../features/CandidatePooling/candidatePoolingSlice';
import VideoProfileReducer from '../features/Setting/VideoProfiling/VideoCreationSlice';
let refreshStatus = 1; // 0 = failed, 1 = all okay, 2 = in progress
/**
 * this function monitors the accessToken expiry and
 * fetches a new accessToken based on refreshToken - only if user is active
 * @returns {function(*): function(*)}
 */

const tokenRefresher = (store) => (next) => (action) => {
  const token = occyLs.getObject('token');
  const authType = occyLs.getObject('authType');
  const expiry = occyLs.getObject('expiry');
  if (!token || token?.startsWith('undefined')) return next(action);
  try {
    const tokenObj =
      authType === 'GOOGLE' || authType === 'MS' ? token : jwtDecode(token);
    const activeTokenExpiry =
      authType === 'GOOGLE' || authType === 'MS'
        ? moment.unix(expiry).diff(moment(), 'minutes')
        : moment.unix(tokenObj.exp).diff(moment(), 'minutes'); // token expiry in minutes

    if (
      tokenObj &&
      refreshStatus === 1 &&
      activeTokenExpiry < 30
    ) {
      refreshStatus = 2;
      const ls = occyLs.getObject('refreshToken');
      const token = occyLs.getObject('token');
      let url =
        authType === 'GOOGLE'
          ? 'google/auth/refresh-auth'
          : authType === 'MS'
          ? 'ms/auth/refresh-auth'
          : 'auth/refresh-auth';
      fetch(`${process.env.REACT_APP_API_URL}/api/${url}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          refreshToken: ls,
          username: tokenObj.username,
        }),
      })
        .then(async (rawResponse) => {
          const rsp = await rawResponse.json();
          if (!rawResponse.ok) {
            throw new Error(rsp?.message || 'Token refresh failed');
          }
          const account = occyLs.getObject('account');
          const currentUnixTime = moment().unix();
          // Add expiryInSeconds to the current time to get the expiry timestamp
          const expire = currentUnixTime + rsp?.expiresIn;
          store.dispatch(
            updateToken({
              accessToken: rsp?.accessToken + '|' + account?.company?.id,
              expiresIn:
                authType === 'GOOGLE' || authType === 'MS'
                  ? expire
                  : rsp?.expiresIn,
            }),
          );
          next(action);
          refreshStatus = 1;
        })
        .catch((e) => {
          console.error(e);
          refreshStatus = 0;
          next(action);
        });
    } else {
      // update user profile information in the global state if the profile is changed
      if (
        action?.meta?.arg?.queryCacheKey ===
          'getProfileInformation(undefined)' &&
        isObject(action?.payload?.account)
      ) {
        store.dispatch(updateProfileState(action?.payload?.account));
      }
      return next(action);
    }
  } catch (e) {
    console.error(e);
    window.location.href = '/logout';
  }
};

let lastErrorTime = 0;
const THROTTLE_TIME = 3000;

const rtkQueryErrorLogger = (store) => (next) => (action) => {
  const now = Date.now();

  // if 401 then redirect to /
  if (
    action?.payload?.status === 401 &&
    !(action?.payload?.data?.message === 'Incorrect username or password.') &&
    !action?.meta?.baseQueryMeta?.request?.url.includes(
      'api/google/auth/claim-grant',
    ) &&
    !action?.meta?.baseQueryMeta?.request?.url.includes(
      'api/ms/auth/claim-grant',
    )
  ) {
    window.location.href = '/logout';
    return;
  }

  // Log a warning and show a toast!
  if (isRejectedWithValue(action)) {
    if (action?.payload?.data?.code === PERMISSION_DENIED_CODE) {
      return next(action);
    }
    console.warn('We got a rejected action!');
    if (whitelistedErrors?.includes(action?.payload?.data?.message)) {
      store.dispatch(setOOOPermission(true));
    }
    if (forceLoginError?.includes(action?.payload?.data?.message)) {
      store.dispatch(setForceLoginPermission(true));
    }
    if (
      !whitelistedErrors?.includes(action?.payload?.data?.message) &&
      !action?.meta?.baseQueryMeta?.request?.url?.includes(
        'api/ai/candidate-summary',
      ) &&
      !action?.meta?.baseQueryMeta?.request?.url?.includes('api/templates') &&
      !(
        action?.meta?.baseQueryMeta?.request?.url?.includes('api/candidates') &&
        action?.meta?.baseQueryMeta?.request?.method === 'PUT'
      ) &&
      now - lastErrorTime > THROTTLE_TIME
    ) {
      const validationErrors = processValidationErrors(action?.payload);
      if (validationErrors?.length > 0) {
        toast.error(validationErrors);
      } else {
        toast.error(
          action?.payload?.data?.message ||
            action?.payload?.error ||
            'Something went wrong!',
        );
      }
      lastErrorTime = now;
    }
  }
  return next(action);
};

const store = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    videoProfile: VideoProfileReducer,
    document: DocumentReducer,
    auth: authReducer,
    permissionGate: permissionGateReducer,
    readOnly: permisssionReducer,
    createJob: jobCreationReducer,
    createEmail: mailCreationReducer,
    createMessage: messageCreationReducer,
    emailTemplate: emailTemplateReducer,
    candidates: candidatesReducer,
    formDesign: formDesignReducer,
    notes: NotesReducer,
    pagination: paginationReducer,
    stages: stagesReducer,
    notificationss: notificationsReducer,
    campaigns: campaignsReducer,
    childCompany: childCompanyReducer,
    childCompanyHeader: childCompanyHeaderReducer,
    headerCredits: creditCompanyReducer,
    WizardDataSlice: WizardDataReducer,
    accounts: AccountReducer,
    candidatePooling: candidatePoolingReducer,
  },
  middleware: (getDefaultMiddleware) => {
    return [
      permissionDeniedListener,
      rtkQueryErrorLogger,
      tokenRefresher,
      ...getDefaultMiddleware({
        serializableCheck: false,
      }).concat(apiSlice.middleware),
    ];
  },
  devTools: process.env.NODE_ENV !== 'production',
});
export default store;
